
import { Button } from "@/components/ui/button";
import "../styles/colors.css";
import { useEffect, useState } from "react";
import { useCallback } from "react";
import Particles from "react-tsparticles";
import type { Container, Engine } from "tsparticles-engine";
import { loadFull } from "tsparticles";
import { useNavigation } from "@/hooks/use-navigation";

export const Hero = () => {
  const navigate = useNavigation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadFull(engine);
  }, []);

  const particlesLoaded = useCallback(async (container: Container | undefined) => {
    console.log("Particles loaded", container);
  }, []);

  useEffect(() => {
    const targetDate = new Date('2026-06-20T00:00:00');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-[80vh] relative bg-black">
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={{
          fullScreen: false,
          background: {
            color: {
              value: "#000000",
            },
          },
          particles: {
            number: {
              value: 30,
              density: {
                enable: true,
                value_area: 800,
              },
            },
            color: {
              value: ["#FF0000", "#FF3333", "#CC0000", "#FFFFFF", "#E60000"],
            },
            shape: {
              type: ["circle", "triangle", "polygon"],
              polygon: {
                sides: 6,
              },
            },
            opacity: {
              value: 0.6,
              random: true,
              animation: {
                enable: true,
                speed: 1,
                minimumValue: 0.1,
                sync: false,
              },
            },
            size: {
              value: 8,
              random: true,
              animation: {
                enable: true,
                speed: 4,
                minimumValue: 0.1,
                sync: false,
              },
            },
            links: {
              enable: true,
              distance: 150,
              color: "#FF0000",
              opacity: 0.4,
              width: 1,
            },
            move: {
              enable: true,
              speed: 2,
              direction: "none",
              random: true,
              straight: false,
              outModes: {
                default: "bounce",
              },
              attract: {
                enable: true,
                rotateX: 600,
                rotateY: 1200,
              },
            },
          },
          interactivity: {
            detectsOn: "canvas",
            events: {
              onHover: {
                enable: true,
                mode: "grab",
              },
              onClick: {
                enable: true,
                mode: "push",
              },
              resize: true,
            },
            modes: {
              grab: {
                distance: 140,
                links: {
                  opacity: 1,
                },
              },
              push: {
                quantity: 4,
              },
            },
          },
          retina_detect: true,
        }}
        className="absolute inset-0"
      />
      <div className="container mx-auto px-4 relative z-10 flex items-center justify-center min-h-[80vh] pb-16">
        <div className="text-center max-w-4xl">
          {/* Main heading */}
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-6">
            İlk Kampüsün.
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-2xl text-white mb-8">
            Kampüs Mentörlük'ün seçkin YKS koçlarıyla, sınav sürecinizi profesyonelce planlayın, hedeflerinize emin adımlarla ilerleyin.
          </p>

          {/* Countdown Timer */}
          <div className="py-6 mb-8">
            <p className="text-lg text-white mb-4">2026 YKS'ye Kalan Süre:</p>
            <div className="grid grid-cols-2 md:flex md:justify-center gap-2 md:gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-4 min-w-[80px] md:min-w-[100px]">
                <div className="text-2xl md:text-3xl font-bold text-white">{timeLeft.days}</div>
                <div className="text-xs md:text-sm text-gray-300">Gün</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-4 min-w-[80px] md:min-w-[100px]">
                <div className="text-2xl md:text-3xl font-bold text-white">{timeLeft.hours}</div>
                <div className="text-xs md:text-sm text-gray-300">Saat</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-4 min-w-[80px] md:min-w-[100px]">
                <div className="text-2xl md:text-3xl font-bold text-white">{timeLeft.minutes}</div>
                <div className="text-xs md:text-sm text-gray-300">Dakika</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-4 min-w-[80px] md:min-w-[100px]">
                <div className="text-2xl md:text-3xl font-bold text-white">{timeLeft.seconds}</div>
                <div className="text-xs md:text-sm text-gray-300">Saniye</div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              onClick={() => navigate('/coaches')}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white w-full sm:w-auto text-lg px-8 py-6"
              size="lg"
            >
              Mentörlere Göz At
            </Button>
            <Button
              onClick={() => navigate('/koc-oner')}
              className="bg-white hover:bg-white/90 text-brand-primary w-full sm:w-auto text-lg px-8 py-6"
              size="lg"
              variant="outline"
            >
              Bana Koç Öner
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
